import type { SolanaNetworkConfig } from "../config/solana";
import { formatSolanaTokenAmount } from "../config/solana";
import type { SolanaJournalRecord } from "../joinsplit-v2/solana";

/** Show the complete immutable payout intent before an explicit recovery send. */
export function pendingSettlementDetails(
  record: SolanaJournalRecord,
  network: SolanaNetworkConfig,
): string[] {
  const intent = record.intent;
  if (!intent)
    return ["Incomplete settlement record; cannot review destinations"];
  const token = network.tokens.find((item) => item.mint === intent.mint);
  const symbol =
    token?.symbol ?? (intent.mint === null ? "SOL" : "token units");
  const amount = (value: bigint) =>
    token ? formatSolanaTokenAmount(value, token.decimals) : value.toString();
  const details = [`Token: ${symbol} (${intent.mint ?? "native SOL"})`];
  const fee = BigInt(intent.fee);
  if (record.kind === "withdraw_batch") {
    for (const item of intent.batch ?? []) {
      details.push(
        `Recipient: ${item.owner} — ${amount(BigInt(item.amount))} ${symbol}`,
      );
    }
    details.push(`Fee: ${amount(fee)} ${symbol}`);
    details.push(
      `Total spent: ${amount(BigInt(intent.withdrawAmount))} ${symbol}`,
    );
    return details;
  }
  if (record.kind === "withdraw") {
    const payout = BigInt(intent.withdrawAmount) - fee;
    details.push(`Recipient: ${intent.recipientOwner ?? "missing"}`);
    details.push(`Payout: ${amount(payout)} ${symbol}`);
    details.push(`Fee: ${amount(fee)} ${symbol}`);
    details.push(
      `Total spent: ${amount(BigInt(intent.withdrawAmount))} ${symbol}`,
    );
    return details;
  }
  if (record.kind === "joinsplit") {
    for (const delivery of record.deliveries.filter(
      (entry) => !entry.keepLocal,
    )) {
      const note = record.outputNotes.find(
        (candidate) =>
          candidate.commitment.toLowerCase() ===
          delivery.commitment.toLowerCase(),
      );
      details.push(
        `Private recipient: ${delivery.recipientMpkHex} — ${note ? amount(note.value) : "unknown"} ${symbol}`,
      );
    }
    return details;
  }
  details.push(
    `Migration of ${record.sourceNullifiers.length} note${record.sourceNullifiers.length === 1 ? "" : "s"}`,
  );
  return details;
}
